// Privacy Policy Modal Component
import React, { useEffect } from 'react';
import './TermsModal.css';

interface PrivacyModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const PrivacyModal: React.FC<PrivacyModalProps> = ({ isOpen, onClose }) => {
    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent background scrolling when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="terms-modal-overlay" onClick={onClose}>
            <div className="terms-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="terms-modal-close" onClick={onClose} aria-label="Close">
                    ✕
                </button>

                <div className="terms-modal-body">
                    <h1>Privacy & GDPR Notice – Deli Feed</h1>
                    <p className="last-updated">Last updated: November 2025</p>

                    <div className="terms-intro">
                        <p>
                            We respect your privacy and comply with the Data Protection Act 2018 and the UK General Data Protection Regulation (UK GDPR).
                        </p>
                    </div>

                    <section>
                        <h2>1. Data We Collect</h2>
                        <p>When you place an order, we collect:</p>
                        <ul>
                            <li>Name</li>
                            <li>Delivery address</li>
                            <li>Phone number</li>
                            <li>Email (if provided)</li>
                            <li>Order details (items, quantities, delivery preferences)</li>
                        </ul>
                        <p>We also use local storage to remember your basket and basic preferences.</p>

                        <div className="contact-info" style={{ marginTop: '1rem' }}>
                            <strong>⚠️ Important: Credit Card Information</strong>
                            <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                                We <strong>never receive, store, or have access to your credit card information</strong>.
                                All online card payments are processed securely by Stripe, a PCI-DSS compliant payment processor.
                                Your card details are transmitted directly to Stripe and never pass through our systems.
                            </p>
                        </div>
                    </section>

                    <section>
                        <h2>2. How We Use Your Data</h2>
                        <p>We use personal data to:</p>
                        <ul>
                            <li>Process and deliver orders</li>
                            <li>Communicate about orders (confirmations, updates)</li>
                            <li>Improve our service and menu offerings</li>
                            <li>Send marketing messages only if you give consent</li>
                        </ul>
                        <p>
                            We do not sell or rent personal data to anyone.
                        </p>
                    </section>

                    <section>
                        <h2>3. Sharing of Data</h2>
                        <p>We share data only with trusted partners required to fulfil orders, such as:</p>
                        <ul>
                            <li><strong>Payment processors:</strong> Stripe (online card payments) and SumUp (card on delivery)</li>
                            <li><strong>Email service:</strong> For order confirmations and updates</li>
                            <li><strong>Delivery systems:</strong> If used in future</li>
                        </ul>
                        <p>
                            All partners are expected to comply with UK data-protection law and maintain appropriate security standards.
                        </p>
                        <p>
                            <strong>Note:</strong> When you pay by card online, your payment information is shared directly with Stripe.
                            We only receive confirmation of payment success or failure, not your card details.
                        </p>
                    </section>

                    <section>
                        <h2>4. Data Retention and Your Rights</h2>
                        <p>
                            We keep order records as long as necessary for legal and accounting purposes (typically up to 7 years as required by HMRC).
                        </p>

                        <p><strong>Your rights under UK GDPR:</strong></p>
                        <ul>
                            <li><strong>Access:</strong> Request a copy of your personal data</li>
                            <li><strong>Correction:</strong> Ask us to correct inaccurate data</li>
                            <li><strong>Deletion:</strong> Request deletion of your data (subject to legal obligations)</li>
                            <li><strong>Portability:</strong> Receive your data in a portable format</li>
                            <li><strong>Objection:</strong> Object to processing for marketing purposes</li>
                            <li><strong>Withdrawal:</strong> Withdraw consent for marketing at any time</li>
                        </ul>

                        <p className="contact-info">
                            To exercise any of these rights, contact us on WhatsApp or mobile: <strong>079 444 88 611</strong>
                        </p>
                    </section>

                    <section>
                        <h2>5. Data Security</h2>
                        <p>
                            We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, loss, or misuse.
                        </p>
                        <p>
                            Our website uses HTTPS encryption for all data transmission.
                            Payment processing is handled by PCI-DSS compliant third parties (Stripe and SumUp).
                        </p>
                    </section>

                    <section>
                        <h2>6. Cookies and Local Storage</h2>
                        <p>
                            Our website uses local storage (in your browser) to keep your basket and basic preferences.
                            This data stays on your device and can be cleared at any time.
                        </p>
                        <p>
                            We may use cookies or analytics tools in the future to improve our service.
                            You can clear or block cookies via your browser settings.
                        </p>
                    </section>

                    <section>
                        <h2>7. Children's Privacy</h2>
                        <p>
                            Our service is not directed at children under 16.
                            We do not knowingly collect data from children.
                        </p>
                    </section>

                    <section>
                        <h2>8. Policy Updates</h2>
                        <p>
                            We may revise this Privacy Notice occasionally to reflect changes in our practices or legal requirements.
                            Any changes will appear on this page with a new effective date.
                        </p>
                        <p>
                            Continued use of our service after changes constitutes acceptance of the updated policy.
                        </p>
                    </section>

                    <section>
                        <h2>9. Contact Us</h2>
                        <p>
                            If you have any questions or concerns about our privacy practices, please contact us:
                        </p>
                        <p className="contact-info">
                            📱 WhatsApp / Mobile: <strong>079 444 88 611</strong>
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PrivacyModal;
